import React, { useEffect, useState } from "react";
import { useUserContext } from "../../utils/UserContext";
import { API_URL } from "../../utils/config";
import { generateAddress } from "../../utils/Utilitats";
import AjudaEntitats from "./AjudaEntitats";
import { BotoAjuda } from "./AjudaModal";

function nomEntitat(entitat) {
  if (!entitat) return "";
  if (["Empresa", "Autonom"].includes(entitat.TipusEntitat)) {
    return entitat.NomComercial?.trim() ? entitat.NomComercial : entitat.Nom;
  }
  const cognoms = [entitat.Cognom1, entitat.Cognom2].filter(Boolean).join(" ");
  return [cognoms, entitat.Nom].filter((x) => x && String(x).trim()).join(", ");
}

function Clientrelacions({ tipus, client, subnav, onCancel, onObrir }) {
  const esTreballs = tipus === "treballs";
  const { empresaSeleccionada } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa ?? null;
  const idClient = client?.idClients ?? null;
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!idEmpresa || !idClient) {
      setFiles([]);
      return;
    }

    let cancelat = false;
    const carregar = async () => {
      setLoading(true);
      setError("");
      try {
        const endpoint = esTreballs ? "Treballs.php" : "Pressupostos.php";
        const response = await fetch(`${API_URL}/${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa, idClient }),
        });
        const data = await response.json();
        if (cancelat) return;
        if (!data.success) {
          setFiles([]);
          setError(data.message || "No s'han pogut carregar les dades.");
          return;
        }
        setFiles(esTreballs ? data.treballs || [] : data.pressupostos || []);
      } catch (err) {
        if (!cancelat) {
          console.error("Error carregant relacions del client:", err);
          setFiles([]);
          setError("S'ha produït un error en carregar les dades.");
        }
      } finally {
        if (!cancelat) setLoading(false);
      }
    };

    carregar();
    return () => {
      cancelat = true;
    };
  }, [esTreballs, idClient, idEmpresa]);

  const titol = esTreballs ? "Treballs" : "Pressupostos";
  const buit = esTreballs ? "Aquest client no té treballs." : "Aquest client no té pressupostos.";

  const filesVista = files.map((fila) => ({
    id: esTreballs ? fila.idTreballs : fila.idPressupostos,
    descripcio: esTreballs ? fila.Descripcio : fila.Comentaris,
    adreca: generateAddress(fila),
    estat: esTreballs ? fila.Estat : fila.EstatGlobal,
  }));

  return (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaEntitats pestanya="relacions" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">
            {esTreballs ? "🔧" : "📄"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">{nomEntitat(client) || "Client"}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Treballs i Pressupostos del client" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          <button type="button" className="dv2-btn dv2-btn--primary" onClick={onObrir}>
            Obrir a {titol}
          </button>
        </div>
      </div>
      {subnav}
      <div className="dv2-entity-body">
        <section className="dv2-card">
          {loading ? (
            <p className="dv2-empty">Carregant…</p>
          ) : error ? (
            <p className="dv2-empty">{error}</p>
          ) : filesVista.length === 0 ? (
            <p className="dv2-empty">{buit}</p>
          ) : isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {filesVista.map((fila) => (
                <article
                  key={fila.id}
                  className="dv2-record"
                  onClick={onObrir}
                  title="Obre la llista completa"
                >
                  <div>
                    <p className="dv2-record-title">{fila.descripcio?.trim() ? fila.descripcio : "—"}</p>
                    <p className="dv2-record-meta">
                      {[fila.adreca, fila.estat].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    <th>Descripció</th>
                    <th>Adreça</th>
                    <th>Estat</th>
                  </tr>
                </thead>
                <tbody>
                  {filesVista.map((fila) => (
                    <tr
                      key={fila.id}
                      onDoubleClick={onObrir}
                      title="Doble clic per obrir la llista completa"
                    >
                      <td className="dv2-cell-clip" title={fila.descripcio || ""}>
                        {fila.descripcio?.trim() ? fila.descripcio : "—"}
                      </td>
                      <td className="dv2-cell-clip" title={fila.adreca || ""}>
                        {fila.adreca || "—"}
                      </td>
                      <td>{fila.estat || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <p className="dv2-hint">
          {isNarrow
            ? `Toca una fitxa o «Obrir a ${titol}» per veure la llista completa.`
            : `El doble clic obre ${esTreballs ? "Treballs" : "Pressupostos"} d'aquest client.`}
        </p>
      </div>
    </div>
  );
}

export default Clientrelacions;
